(function () {
  'use strict';

  var READY_EVENT = 'fit:accordion-gallery:ready';

  function notifyReady() {
    document.dispatchEvent(new CustomEvent(READY_EVENT));
  }

  if (window.FitAccordionGallery) {
    notifyReady();
    return;
  }

  var DEFAULT_TOGGLE_EVENT =
    (window.FitCollapsibleGroup && window.FitCollapsibleGroup.toggleEventName) || 'fit:collapsible-toggle';
  var DEFAULT_SUPPRESS_MS = 140;

  function toArray(nodes) {
    return Array.prototype.slice.call(nodes || []);
  }

  function getRows(root, rowSelector) {
    if (!(root instanceof Element)) return [];
    return toArray(root.querySelectorAll(rowSelector));
  }

  function getRowId(row, rowAttribute) {
    return row instanceof Element ? row.getAttribute(rowAttribute) : null;
  }

  function getDetails(row) {
    return row instanceof Element ? row.querySelector('details') : null;
  }

  function getActiveRow(rows) {
    for (var index = 0; index < rows.length; index += 1) {
      var details = getDetails(rows[index]);
      if (details instanceof HTMLDetailsElement && details.open) return rows[index];
    }

    return rows[0] || null;
  }

  function activateByData(container, itemSelector, activeClass, itemAttribute, activeId) {
    if (!(container instanceof Element)) return -1;

    var items = toArray(container.querySelectorAll(itemSelector));
    var activeIndex = -1;

    items.forEach(function (item, index) {
      var isActive = item.getAttribute(itemAttribute) === activeId;
      item.classList.toggle(activeClass, isActive);
      item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      if (isActive) activeIndex = index;
    });

    return activeIndex;
  }

  function createController(root, options) {
    if (!(root instanceof Element)) return null;

    var rowSelector = options.rowSelector;
    var rowAttribute = options.rowAttribute;
    var onActiveRowChange = typeof options.onActiveRowChange === 'function' ? options.onActiveRowChange : null;
    var onStructureChange = typeof options.onStructureChange === 'function' ? options.onStructureChange : null;
    var structureDebounce = typeof options.structureDebounce === 'number' ? options.structureDebounce : 120;

    var activeRowId = null;
    var destroyed = false;
    var structureObserver = null;
    var structureTimer = null;
    var suppressUntil = 0;

    function getRowById(rowId) {
      var rows = getRows(root, rowSelector);
      for (var index = 0; index < rows.length; index += 1) {
        if (getRowId(rows[index], rowAttribute) === rowId) return rows[index];
      }
      return null;
    }

    function getRowInfo(explicitRowId) {
      var rows = getRows(root, rowSelector);
      if (!rows.length) return null;

      var row = explicitRowId ? getRowById(explicitRowId) : null;
      if (!(row instanceof Element)) row = getActiveRow(rows);
      if (!(row instanceof Element)) return null;

      return {
        row: row,
        rowId: getRowId(row, rowAttribute),
        index: rows.indexOf(row),
        details: getDetails(row),
        rows: rows,
      };
    }

    function notifyActiveRowChange(reason, explicitRowId, force) {
      var info = getRowInfo(explicitRowId);
      if (!info || !info.rowId) return null;
      if (!force && info.rowId === activeRowId) return info;

      activeRowId = info.rowId;
      info.reason = reason || 'sync';

      if (onActiveRowChange) onActiveRowChange(info);
      return info;
    }

    function closeSiblingRows(row) {
      var accordionRoot = row instanceof Element ? row.closest('[data-single-open]') : null;
      if (!(accordionRoot instanceof Element)) return;
      if (String(accordionRoot.getAttribute('data-single-open') || '').toLowerCase() !== 'true') return;

      toArray(accordionRoot.children).forEach(function (child) {
        if (!(child instanceof Element) || child === row || !child.matches('collapsible-group')) return;

        var details = getDetails(child);
        if (details instanceof HTMLDetailsElement && details.open) details.open = false;
      });
    }

    function activateRow(rowId, reason, forceSync) {
      var row = getRowById(rowId);
      var details = getDetails(row);
      if (!(row instanceof Element) || !(details instanceof HTMLDetailsElement)) return false;

      if (!details.open) {
        closeSiblingRows(row);
        details.open = true;
      }

      notifyActiveRowChange(reason || 'activate', rowId, forceSync !== false);
      return true;
    }

    function activateRowIndex(index, reason, forceSync) {
      var rows = getRows(root, rowSelector);
      var row = rows[index];
      var rowId = getRowId(row, rowAttribute);
      if (!rowId) return false;

      return activateRow(rowId, reason, forceSync);
    }

    function handleToggle(event) {
      if (destroyed || !event.detail || event.detail.open !== true) return;
      if (!(event.target instanceof Element) || !root.contains(event.target)) return;

      var row = event.target.matches(rowSelector) ? event.target : event.target.closest(rowSelector);
      var rowId = getRowId(row, rowAttribute);
      if (!rowId) return;

      notifyActiveRowChange('toggle', rowId, false);
    }

    function handleBlockSelect(event) {
      var target = event && event.target;
      if (destroyed || !(target instanceof Element) || !root.contains(target)) return;

      var row = target.matches(rowSelector) ? target : target.closest(rowSelector);
      var rowId = getRowId(row, rowAttribute);
      if (!rowId) return;

      activateRow(rowId, 'block-select');
    }

    function observeStructure() {
      if (!onStructureChange) return;

      var observeTarget =
        typeof options.getObserveTarget === 'function' ? options.getObserveTarget(root) : root;
      if (!(observeTarget instanceof Element)) return;

      structureObserver = new MutationObserver(function (records) {
        if (destroyed || Date.now() < suppressUntil) return;

        if (typeof options.shouldIgnoreMutations === 'function') {
          var hasRelevantMutation = records.some(function (record) {
            return !options.shouldIgnoreMutations(record);
          });
          if (!hasRelevantMutation) return;
        }

        clearTimeout(structureTimer);
        structureTimer = setTimeout(function () {
          if (!destroyed) onStructureChange();
        }, structureDebounce);
      });

      structureObserver.observe(observeTarget, { childList: true, subtree: true });
    }

    root.addEventListener(DEFAULT_TOGGLE_EVENT, handleToggle);

    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:block:select', handleBlockSelect);
    }

    observeStructure();

    return {
      root: root,
      rowSelector: rowSelector,
      rowAttribute: rowAttribute,
      getRows: function () {
        return getRows(root, rowSelector);
      },
      getRowId: function (row) {
        return getRowId(row, rowAttribute);
      },
      getRowDetails: getDetails,
      getRowInfo: getRowInfo,
      getActiveRowId: function () {
        var info = getRowInfo();
        return info ? info.rowId : null;
      },
      getRowIndex: function (rowId) {
        var info = getRowInfo(rowId);
        return info ? info.index : -1;
      },
      activateRow: activateRow,
      activateRowIndex: activateRowIndex,
      sync: function (reason) {
        return notifyActiveRowChange(reason || 'sync', null, true);
      },
      suppressMutations: function (durationMs) {
        suppressUntil = Date.now() + (durationMs || DEFAULT_SUPPRESS_MS);
      },
      withSuppressedMutations: function (callback, durationMs) {
        this.suppressMutations(durationMs);
        return typeof callback === 'function' ? callback() : null;
      },
      activateByData: function (container, itemSelector, activeClass, itemAttribute, activeId) {
        return activateByData(container, itemSelector, activeClass, itemAttribute, activeId);
      },
      destroy: function () {
        destroyed = true;
        root.removeEventListener(DEFAULT_TOGGLE_EVENT, handleToggle);
        document.removeEventListener('shopify:block:select', handleBlockSelect);
        clearTimeout(structureTimer);
        structureObserver && structureObserver.disconnect();
      },
    };
  }

  window.FitAccordionGallery = {
    createController: createController,
    activateByData: activateByData,
    getRows: getRows,
    getActiveRow: getActiveRow,
  };

  notifyReady();
})();
